import { useState, useEffect, useMemo, Fragment } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { brl, num, km } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wallet, Lock, Unlock, RefreshCw, Sparkles, Trash2, Plus, Save, SlidersHorizontal, FileText, Trophy, Pencil, Check, X, UserCog, FileSpreadsheet } from "lucide-react";
import { jsPDF } from "jspdf";
import { computeAutoBonus } from "@/lib/bonusConfig";
import { useContractTypes } from "@/lib/useContractTypes";
import { useMinsFor, useBaseConfigs, minsForWith } from "@/lib/useBaseMins";
import { usePayBases, filterPayrollByPayBase } from "@/lib/usePayBases";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { getBaseId } from "@/lib/api";
import { usePayrollSettings } from "@/context/PayrollSettingsContext";
import { useBase } from "@/context/BaseContext";
import { useBaseSalaries, salaryBaseOf } from "@/lib/useBaseSalaries";
import { useBaseComponents, baseCompMaps, baseItemsFor, applyBaseComponents, pjRatesOf } from "@/lib/baseComponents";
import panelApi from "@/lib/panelApi";
import { useAuth } from "@/context/AuthContext";
import { isGestor } from "@/lib/permissions";
import { CollaboratorDetailDialog, buildAllPdf } from "@/components/CollaboratorDetailDialog";
import { ThirteenthPanel } from "@/components/ThirteenthPanel";
import { useReportLogo, addPdfLogo } from "@/lib/useLogo";
import { toXlsxRow, vehicleOf, periodText, downloadPayrollXlsx, sharePayrollWhatsApp, shareXlsxWhatsApp } from "@/lib/payrollXlsx";
import { WhatsAppMenu } from "@/components/WhatsAppMenu";
import { MessageCircle } from "lucide-react";
import { useCompOverrides, ovKey } from "@/lib/compOverrides";
import { isBaseSalaryItem, hasBaseFlag } from "@/lib/baseSalary";

const now = new Date();
const pad = (n) => String(n).padStart(2, "0");

const SOURCES = [
  { v: "fixed", label: "Valor fixo" },
  { v: "meal", label: "Vale alimentação (auto)" },
  { v: "commute", label: "Auxílio deslocamento (auto)" },
  { v: "fuel", label: "Combustível (auto)" },
  { v: "bonus", label: "Bônus" },
  { v: "bonus_auto", label: "Bônus (auto)" },
];
const LOCKED_SOURCES = ["meal", "commute", "fuel", "bonus_auto"];
const ETYPES = [
  { key: "ciclista", label: "Ciclista CLT" },
  { key: "ciclista_pj", label: "Ciclista PJ", section: "ciclista_pj" },
  { key: "motociclista", label: "Motoboy CLT" },
  { key: "motociclista_pj", label: "Motoboy PJ", section: "motociclista_pj" },
  { key: "administrativo", label: "Administrativo" },
];
const PJ_SECTIONS = ["ciclista_pj", "motociclista_pj"];

const KINDS = [
  { v: "salary", label: "Salário" },
  { v: "benefit", label: "Benefício" },
  { v: "discount", label: "Desconto" },
  { v: "tax", label: "Imposto" },
];

const QZ_OPTIONS = [
  { v: "ambas", label: "Ambas" },
  { v: "1", label: "1ª Quinzena" },
  { v: "2", label: "2ª Quinzena" },
];

// Resolve a classificação REAL de um componente a partir da config salva (por id)
// + marcadores locais de imposto. Salário/Benefício não são descontos; Desconto e
// Imposto são deduzidos do colaborador (is_discount), diferenciados pelo tax_items.
const makeClassifier = (configById = {}, taxItems = {}) => (it) => {
  const c = configById[it.id];
  const isDisc = c ? !!c.is_discount : !!it.is_discount;
  if (isDisc) return taxItems[it.id] ? "tax" : "discount";
  const k = (c ? c.kind : it.kind) || (it.source === "fixed" ? "salary" : "benefit");
  return k === "salary" ? "salary" : "benefit";
};

const groupItems = (items = [], classify) => {
  const cl = classify || ((i) => (i.is_discount ? "discount" : (i.kind === "salary" || (!i.kind && i.source === "fixed") ? "salary" : "benefit")));
  const salario = items.filter((i) => cl(i) === "salary");
  const beneficios = items.filter((i) => cl(i) === "benefit");
  const descontos = items.filter((i) => cl(i) === "discount");
  const impostos = items.filter((i) => cl(i) === "tax");
  const sum = (a) => a.reduce((s, x) => s + (x.value || 0), 0);
  return { salario, beneficios, descontos, impostos, sSal: sum(salario), sBen: sum(beneficios), sDesc: sum(descontos), sImp: sum(impostos) };
};

function ConfigEditor({ etype, label, initial, onSaved, section, rate, baseId, baseName, baseCfg, baseSalary, onBaseSaved }) {
  const { settings, save: savePanel } = usePayrollSettings();
  const [items, setItems] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  const [pjRate, setPjRate] = useState(rate ?? 0);
  const mode = baseId ? (baseCfg ? "base" : "fallback") : "global";
  useEffect(() => { setPjRate(baseCfg?.pj_rate ?? rate ?? 0); }, [rate, baseCfg]);
  useEffect(() => {
    if (baseCfg) { setItems(baseCfg.items.map((c) => ({ ...c }))); return; }
    const disabled = settings.disabled_items || {};
    const auto = settings.auto_bonus_items || {};
    const tax = settings.tax_items || {};
    const qzi = settings.quinzena_items || {};
    const bsf = settings.base_salary_items || {};
    const anyFlag = hasBaseFlag(initial, bsf);
    let salDone = false;
    setItems((initial || []).map((it) => {
      const isAutoSrc = it.id && auto[it.id];
      const isTax = it.id ? !!tax[it.id] : false;
      const cls = isAutoSrc ? "benefit"
        : isTax ? "tax"
        : it.is_discount ? "discount"
        : (it.kind === "salary" ? "salary" : it.kind === "benefit" ? "benefit" : (it.source === "fixed" ? "salary" : "benefit"));
      // Base sem config própria: mostra a global, com o Salário Base da base (se já definido).
      const isSal = !salDone && isBaseSalaryItem(it, { flags: bsf, anyFlag, isSalary: cls === "salary" });
      if (isSal) salDone = true;
      return {
        ...it,
        is_base_salary: isSal,
        value: isSal && baseId && baseSalary != null ? baseSalary : it.value,
        enabled: it.id ? !disabled[it.id] : true,
        qz: it.id && qzi[it.id] ? String(qzi[it.id]) : "ambas",
        source: isAutoSrc ? "bonus_auto" : it.source,
        cls,
      };
    }));
  }, [initial, settings, baseCfg, baseId, baseSalary]);

  const update = (i, patch) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const add = () => setItems((arr) => [...arr, { id: `new-${Date.now()}`, label: "", source: "fixed", value: 0, cls: "salary", enabled: true, qz: "ambas" }]);

  const saveBase = async () => {
    setBusy(true);
    try {
      const payload = { items: items.map((it) => ({ id: it.id, label: it.label, source: it.source, cls: it.cls || "salary", qz: it.qz || "ambas", value: Number(it.value) || 0, enabled: it.enabled !== false, is_base_salary: !!it.is_base_salary && it.source === "fixed" && (it.cls || "salary") === "salary" })) };
      if (section) payload.pj_rate = Number(pjRate) || 0;
      await panelApi.put(`/panel/base-components/${baseId}/${etype}`, payload);
      await onBaseSaved();
      toast.success(`Configuração de ${label} salva só para ${baseName}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const resetBase = async () => {
    setBusy(true);
    try {
      await panelApi.delete(`/panel/base-components/${baseId}/${etype}`);
      await onBaseSaved();
      toast.success(`${baseName} voltou a usar a configuração global de ${label}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    if (baseId) return saveBase();
    setBusy(true);
    try {
      // O upstream não aceita a origem "bonus_auto" (422): gravamos como "bonus" (valor 0)
      // e marcamos o item como automático localmente (por id).
      const payload = { items: items.map((it) => {
        const auto = it.source === "bonus_auto";
        const cls = auto ? "benefit" : (it.cls || "salary");
        const isDisc = cls === "discount" || cls === "tax";
        return {
          id: it.id,
          label: it.label,
          source: auto ? "bonus" : it.source,
          value: auto ? 0 : (Number(it.value) || 0),
          is_discount: isDisc,
          // O upstream só conhece salary/benefit; "discount"/"tax" são inferidos de is_discount + tax_items.
          kind: cls === "salary" ? "salary" : "benefit",
        };
      }) };
      if (section) payload.valor_por_entrega = Number(pjRate) || 0;
      await api.put(section ? `/admin/payroll-settings/${section}` : `/admin/payroll/config/${etype}`, payload);
      // Reconcilia marcadores usando os ids reais retornados (mesma ordem enviada) e
      // persiste o estado no backend (não em localStorage) para sobreviver a deploy/dispositivos.
      let saved = items;
      try {
        const res = await api.get(section ? "/admin/payroll-settings" : "/admin/payroll/config");
        saved = (section ? res.data?.sections?.[section]?.items : res.data?.configs?.[etype]?.items) || items;
      } catch (_) {}
      const disabled = { ...(settings.disabled_items || {}) };
      const auto = { ...(settings.auto_bonus_items || {}) };
      const tax = { ...(settings.tax_items || {}) };
      const qzi = { ...(settings.quinzena_items || {}) };
      const bsf = { ...(settings.base_salary_items || {}) };
      items.forEach((it, i) => {
        const realId = (saved[i] && saved[i].id) || (String(it.id || "").startsWith("new-") ? null : it.id);
        if (!realId) return;
        if (it.is_base_salary && it.source === "fixed" && (it.cls || "salary") === "salary") bsf[realId] = true; else delete bsf[realId];
        if (it.enabled === false) disabled[realId] = true; else delete disabled[realId];
        if (it.source === "bonus_auto") auto[realId] = true; else delete auto[realId];
        if (it.cls === "tax") tax[realId] = true; else delete tax[realId];
        if (it.qz === "1" || it.qz === "2") qzi[realId] = Number(it.qz); else delete qzi[realId];
      });
      await savePanel({ disabled_items: disabled, auto_bonus_items: auto, tax_items: tax, quinzena_items: qzi, base_salary_items: bsf });
      toast.success(`Configuração de ${label} salva`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`payroll-config-${etype}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-slate-100">{label}</h3>
          {mode === "base" && <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-300" data-testid={`config-${etype}-scope`}>Configuração da base · {baseName}</span>}
          {mode === "fallback" && <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-300" data-testid={`config-${etype}-scope`}>Exibindo a global · {baseName} sem configuração própria</span>}
          {mode === "global" && <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-xs font-semibold text-slate-300" data-testid={`config-${etype}-scope`}>Configuração global</span>}
        </div>
        <Button size="sm" variant="outline" className="border-slate-700 text-slate-300" onClick={add} data-testid={`config-add-${etype}`}>
          <Plus className="h-4 w-4" /> Componente
        </Button>
      </div>
      <div className="overflow-x-auto">
      <div className="min-w-[860px] space-y-2">
        {items.length === 0 && !section && <p className="text-sm text-slate-500">Nenhum componente. Adicione salário base, benefícios ou descontos.</p>}
        {(items.length > 0 || section) && (
          <div className="grid grid-cols-12 gap-2 px-1 text-[10px] uppercase tracking-wider text-slate-500">
            <span className="col-span-2">Descrição</span>
            <span className="col-span-2">Origem</span>
            <span className="col-span-2">Classificação</span>
            <span className="col-span-2">Quinzena</span>
            <span className="col-span-2">Valor</span>
            <span className="col-span-1 text-center">Ativo</span>
            <span className="col-span-1" />
          </div>
        )}
        {section && (
          <div className="grid grid-cols-12 items-center gap-2" data-testid={`config-${etype}-pjrate-row`}>
            <Input value="Valor por entrega" disabled className="col-span-2 border-slate-700 bg-slate-950 text-slate-100 disabled:opacity-80" />
            <Input value="Por entrega" disabled className="col-span-2 border-slate-700 bg-slate-950 text-slate-100 disabled:opacity-80" />
            <Input value="Salário" disabled className="col-span-2 border-slate-700 bg-slate-950 text-slate-100 disabled:opacity-80" />
            <Input value="Ambas" disabled className="col-span-2 border-slate-700 bg-slate-950 text-slate-100 disabled:opacity-80" />
            <Input type="number" step="0.01" min="0" value={pjRate} onChange={(e) => setPjRate(e.target.value)}
              data-testid={`config-${etype}-pjrate`} className="col-span-2 border-slate-700 bg-slate-950 text-slate-100" />
            <span className="col-span-2 text-center text-[10px] uppercase tracking-wider text-slate-500">fixo</span>
          </div>
        )}
        {items.map((it, i) => (
          <div key={it.id || i} className={it.enabled === false ? "opacity-60" : ""}>
          <div className="grid grid-cols-12 items-center gap-2">
            <Input value={it.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Descrição"
              data-testid={`config-${etype}-label-${i}`} className="col-span-2 border-slate-700 bg-slate-950 text-slate-100" />
            <div className="col-span-2">
              <Select value={it.source} onValueChange={(v) => update(i, v === "bonus_auto" ? { source: v, cls: "benefit", value: 0 } : { source: v })}>
                <SelectTrigger data-testid={`config-${etype}-source-${i}`} className="border-slate-700 bg-slate-950 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  {SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Select value={it.cls || "salary"} onValueChange={(v) => update(i, { cls: v })} disabled={it.source === "bonus_auto"}>
                <SelectTrigger data-testid={`config-${etype}-kind-${i}`} className="border-slate-700 bg-slate-950 text-slate-100 disabled:opacity-50"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  {KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Select value={it.qz || "ambas"} onValueChange={(v) => update(i, { qz: v })}>
                <SelectTrigger data-testid={`config-${etype}-quinzena-${i}`} className="border-slate-700 bg-slate-950 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  {QZ_OPTIONS.map((q) => <SelectItem key={q.v} value={q.v}>{q.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input type="number" step="0.01" value={it.source === "bonus_auto" ? "" : it.value}
              onChange={(e) => update(i, { value: e.target.value })}
              disabled={LOCKED_SOURCES.includes(it.source)}
              placeholder={it.source === "bonus_auto" ? "auto" : ""}
              data-testid={`config-${etype}-value-${i}`}
              className="col-span-2 border-slate-700 bg-slate-950 text-slate-100 disabled:opacity-50" />
            <div className="col-span-1 flex justify-center">
              <Switch checked={it.enabled !== false} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`config-${etype}-active-${i}`} title="Ativo na folha" />
            </div>
            <button onClick={() => remove(i)} className="col-span-1 flex justify-center text-slate-500 hover:text-rose-400" data-testid={`config-${etype}-del-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {it.source === "fixed" && (it.cls || "salary") === "salary" && (
            <label className="mt-1 flex w-fit cursor-pointer items-center gap-2 pl-1 text-xs text-slate-400" data-testid={`config-${etype}-basesal-${i}`}>
              <input type="radio" name={`basesal-${etype}`} checked={!!it.is_base_salary} onChange={() => setItems((arr) => arr.map((x, idx) => ({ ...x, is_base_salary: idx === i })))} className="accent-orange-500" />
              {it.is_base_salary ? <span className="font-semibold text-orange-300">Salário Base da categoria</span> : "Marcar como Salário Base"}
            </label>
          )}
          {(it.cls === "discount" || it.cls === "tax") && (
            <div className="mt-1 flex items-center gap-2 pl-1" data-testid={`config-${etype}-clsnote-${i}`}>
              {it.cls === "tax" ? (
                <span className="text-xs text-amber-300/80">Imposto <span className="text-slate-500">(retido do colaborador e repassado ao governo; não impacta o Custo Líquido em Custo Operacional)</span></span>
              ) : (
                <span className="text-xs text-rose-300/80">Desconto <span className="text-slate-500">(subtrai do Custo Líquido da empresa em Custo Operacional)</span></span>
              )}
            </div>
          )}
          </div>
        ))}
      </div>
      </div>
      {mode === "fallback" && <p className="mt-3 text-xs text-amber-300/80" data-testid={`config-${etype}-fallback-note`}>Ao salvar, estes componentes passam a valer só para {baseName}. As outras bases continuam com a configuração global.</p>}
      {mode === "global" && <p className="mt-3 text-xs text-slate-500">Vale para todas as bases sem configuração própria. Para configurar uma base, escolha-a no filtro do topo.</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy} className="bg-orange-500 text-white hover:bg-orange-600" data-testid={`config-save-${etype}`}>
          <Save className="h-4 w-4" /> Salvar {label}{baseId ? ` · ${baseName}` : ""}
        </Button>
        {mode === "base" && <Button variant="outline" disabled={busy} onClick={resetBase} className="border-slate-700 text-slate-300" data-testid={`config-${etype}-use-global`}>Usar configuração global</Button>}
      </div>
    </div>
  );
}

function TiersEditor() {
  const { data, loading, error, refetch } = useFetch("/admin/bonus-tiers");
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTiers(data?.tiers || []); }, [data]);

  const update = (i, patch) => setTiers((a) => a.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const remove = (i) => setTiers((a) => a.filter((_, idx) => idx !== i));
  const add = () => setTiers((a) => [...a, { id: `new-${Date.now()}`, name: "", value: 0 }]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        tiers: tiers.map((t) => {
          const o = { name: (t.name || "").trim(), value: Number(t.value) || 0 };
          if (t.id && !String(t.id).startsWith("new-")) o.id = t.id;
          return o;
        }).filter((t) => t.name),
      };
      await api.put("/admin/bonus-tiers", payload);
      toast.success("Categorias de desempenho salvas");
      await refetch();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="bonus-tiers-editor">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-100"><Trophy className="h-4 w-4 text-amber-400" /> Categorias de desempenho (bônus)</h3>
        <Button size="sm" variant="outline" className="border-slate-700 text-slate-300" onClick={add} data-testid="tier-add">
          <Plus className="h-4 w-4" /> Categoria
        </Button>
      </div>
      <p className="mb-4 text-xs text-slate-500">Ex.: Bronze, Prata, Ouro. O valor é o bônus aplicado ao colaborador que estiver na categoria.</p>
      <div className="space-y-2">
        {tiers.length === 0 && <p className="text-sm text-slate-500">Nenhuma categoria. Adicione Bronze, Prata, Ouro…</p>}
        {tiers.map((t, i) => (
          <div key={t.id || i} className="grid grid-cols-12 items-center gap-2">
            <Input value={t.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Nome (ex.: Ouro)"
              data-testid={`tier-name-${i}`} className="col-span-7 border-slate-700 bg-slate-950 text-slate-100" />
            <div className="col-span-4 flex items-center gap-1">
              <span className="text-xs text-slate-500">R$</span>
              <Input type="number" step="0.01" min="0" value={t.value} onChange={(e) => update(i, { value: e.target.value })}
                data-testid={`tier-value-${i}`} className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <button onClick={() => remove(i)} className="col-span-1 flex justify-center text-slate-500 hover:text-rose-400" data-testid={`tier-del-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={busy} className="mt-4 bg-orange-500 text-white hover:bg-orange-600" data-testid="tier-save">
        <Save className="h-4 w-4" /> Salvar categorias
      </Button>
    </div>
  );
}

function PeriodView() {
  const { user } = useAuth();
  const logo = useReportLogo();
  const { bases: allBases, selectedBaseId: curBaseId } = useBase();
  const gestor = isGestor(user);
  const [month, setMonth] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [period, setPeriodState] = useState(() => { const v = sessionStorage.getItem("folha-period"); return v == null ? 1 : (["1", "2"].includes(v) ? Number(v) : v); }); // 1 | 2 | "mensal" | "13-1" | "13-2"
  const setPeriod = (v) => { sessionStorage.setItem("folha-period", String(v)); setPeriodState(v); };
  const is13 = String(period).startsWith("13-");
  const quinzenaParam = period === "mensal" || is13 ? {} : { quinzena: period };
  // Folha fechada: todos os dados do cálculo vêm do snapshot salvo no fechamento (valores congelados).
  const lockParams = { month, period: String(period), base: getBaseId() || "" };
  const lockQ = useQuery({ queryKey: ["payroll-lock", lockParams], enabled: !is13, queryFn: async () => (await panelApi.get("/panel/payroll-lock", { params: lockParams })).data?.lock || null });
  const lock = is13 ? null : lockQ.data || null;
  const snap = useMemo(() => {
    let s = null;
    try { s = lock ? JSON.parse(lock.snapshot_json) : null; } catch { return null; }
    if (!s || !lock.inherited || !lockParams.base) return s;
    // Fechamento de "Todas as bases" visto numa base: só os colaboradores dessa base.
    const ids = new Set((s.collabs?.collaborators || []).filter((c) => (c.base_ids || []).includes(lockParams.base)).map((c) => c.id));
    const pay = (p) => (p?.payroll ? { ...p, payroll: { ...p.payroll, lines: (p.payroll.lines || []).filter((l) => ids.has(l.user_id)) } } : p);
    const rows = (r) => (r ? { ...r, rows: (r.rows || []).filter((x) => ids.has(x.id)) } : r);
    return { ...s, monthly: pay(s.monthly), payQ1: pay(s.payQ1), payQ2: pay(s.payQ2), reports1: rows(s.reports1), reports2: rows(s.reports2),
      benefitsReport: s.benefitsReport ? { ...s.benefitsReport, drivers: (s.benefitsReport.drivers || []).filter((d) => ids.has(d.id)) } : s.benefitsReport };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lock]);
  const inheritedLock = !!lock?.inherited;
  const fz = (k, live) => (snap ? { ...live, data: snap[k] ?? null, loading: false, error: null } : live);
  const ovQ = useCompOverrides(month);
  const ovs = snap ? (snap.compOverrides || {}) : (ovQ.data || {});
  const { data: liveMonthly, loading: liveLoading, error, refetch } = useFetch("/admin/payroll", { month, ...quinzenaParam });
  const { data: livePayBases } = usePayBases();
  const payBases = snap ? (snap.payBases || {}) : livePayBases;
  const monthlyRaw = snap ? snap.monthly : liveMonthly;
  const monthlyData = useMemo(() => filterPayrollByPayBase(monthlyRaw, payBases), [monthlyRaw, payBases]);
  const loading = liveLoading || (!is13 && lockQ.isLoading);
  const payQ1Live = useFetch("/admin/payroll", { month, quinzena: 1 });
  const payQ2Live = useFetch("/admin/payroll", { month, quinzena: 2 });
  const q1Raw = fz("payQ1", payQ1Live), q2Raw = fz("payQ2", payQ2Live);
  const q1Data = useMemo(() => filterPayrollByPayBase(q1Raw.data, payBases), [q1Raw.data, payBases]);
  const q2Data = useMemo(() => filterPayrollByPayBase(q2Raw.data, payBases), [q2Raw.data, payBases]);
  const payQ1 = { ...q1Raw, data: q1Data }, payQ2 = { ...q2Raw, data: q2Data };
  const yr = Number(month.slice(0, 4)), mo = Number(month.slice(5, 7));
  const live = {
    reports1: useFetch("/admin/reports", { year: yr, month: mo, quinzena: 1 }),
    reports2: useFetch("/admin/reports", { year: yr, month: mo, quinzena: 2 }),
    faltas: useFetch("/admin/faltas"),
    cfg: useFetch("/config"),
    tiersData: useFetch("/admin/bonus-tiers"),
    payCfg: useFetch("/admin/payroll/config"),
    benefitsReport: useFetch("/admin/benefits-report", { year: yr, month: mo }),
    pjSettings: useFetch("/admin/payroll-settings"),
    collabs: useFetch("/admin/collaborators"),
  };
  const { reports1, reports2, faltas, cfg, tiersData, payCfg, benefitsReport, pjSettings, collabs } = Object.fromEntries(Object.entries(live).map(([k, v]) => [k, fz(k, v)]));
  const salOverride = useMemo(() => {
    const m = {};
    (collabs.data?.collaborators || []).forEach((c) => { if (c.salary_override != null) m[c.id] = Number(c.salary_override); });
    return m;
  }, [collabs.data]);
  const [salEdit, setSalEdit] = useState(null); // { uid, value }
  const [salBusy, setSalBusy] = useState(false);
  const saveSalary = async (uid, value) => {
    if (value !== null && (value === "" || Number(value) < 0 || Number.isNaN(Number(value)))) { toast.error("Informe um valor válido"); return; }
    setSalBusy(true);
    try {
      await api.put(`/admin/collaborators/${uid}/salary-override`, { value: value === null ? null : Number(value) });
      toast.success(value === null ? "Salário voltou ao padrão da categoria" : "Salário base individual salvo");
      setSalEdit(null);
      await collabs.refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setSalBusy(false); }
  };
  const isMensal = period === "mensal";
  const mergedRows = useMemo(() => {
    const map = {};
    [...(reports1.data?.rows || []), ...(reports2.data?.rows || [])].forEach((r) => {
      const e = map[r.id] || (map[r.id] = { id: r.id, name: r.name, deliveries: 0, km: 0, reimbursement: 0 });
      e.deliveries += r.deliveries || 0; e.km += r.km || 0; e.reimbursement += r.reimbursement || 0;
      if (r.name) e.name = r.name;
    });
    return Object.values(map);
  }, [reports1.data, reports2.data]);
  const reports = isMensal
    ? { data: { rows: mergedRows, label: "Mensal (1ª + 2ª quinzena)" }, loading: reports1.loading || reports2.loading, error: reports1.error || reports2.error, refetch: () => { reports1.refetch(); reports2.refetch(); } }
    : (period === 1 ? reports1 : reports2);
  const periodLabel = isMensal ? "Mensal" : (period === 1 ? "1a quinzena" : "2a quinzena");
  const { settings: liveSettings } = usePayrollSettings();
  const settings = snap?.settings || liveSettings;
  const { configById: configByIdG, etypeOf: etypeOfG } = useMemo(() => {
    const m = {}, et = {};
    Object.entries(payCfg.data?.configs || {}).forEach(([k, c]) => (c.items || []).forEach((it) => { if (it.id) { m[it.id] = it; et[it.id] = k; } }));
    PJ_SECTIONS.forEach((s) => (pjSettings.data?.sections?.[s]?.items || []).forEach((it) => { if (it.id) { m[it.id] = it; et[it.id] = s; } }));
    return { configById: m, etypeOf: et };
  }, [payCfg.data, pjSettings.data]);
  // Config própria por base + categoria: itens com id próprio (b:base:categoria:id) somados aos globais.
  const liveBaseComps = useBaseComponents().data;
  const baseComps = snap ? (snap.baseComponents || {}) : (liveBaseComps || {});
  const baseMaps = useMemo(() => baseCompMaps(baseComps), [baseComps]);
  const configById = useMemo(() => ({ ...configByIdG, ...baseMaps.configById }), [configByIdG, baseMaps]);
  const etypeOf = useMemo(() => ({ ...etypeOfG, ...baseMaps.etypeOf }), [etypeOfG, baseMaps]);
  const liveBaseSalaries = useBaseSalaries().data;
  const baseSalaries = snap ? (snap.baseSalaries || {}) : (liveBaseSalaries || {});
  const collabById = useMemo(() => Object.fromEntries((collabs.data?.collaborators || []).map((c) => [c.id, c])), [collabs.data]);
  // Nível 2: salário da categoria na base (por item de salário → categoria).
  const baseSalOf = (uid, itemId) => {
    const b = salaryBaseOf(uid, lockParams.base, payBases, collabById);
    const v = b ? baseSalaries[b]?.[etypeOf[itemId]] : null;
    return v != null ? Number(v) : null;
  };
  const cfgBaseOf = (uid) => salaryBaseOf(uid, lockParams.base, payBases, collabById);
  const pjRates = useMemo(() => pjRatesOf(pjSettings.data), [pjSettings.data]);
  const bMonthly = useMemo(() => applyBaseComponents(monthlyData, { baseComps, etypeOf: etypeOfG, baseOf: cfgBaseOf, pj: pjRates }), [monthlyData, baseComps, etypeOfG, collabById, payBases, pjRates]); // eslint-disable-line react-hooks/exhaustive-deps
  const bQ1 = useMemo(() => applyBaseComponents(payQ1.data, { baseComps, etypeOf: etypeOfG, baseOf: cfgBaseOf, pj: pjRates }), [payQ1.data, baseComps, etypeOfG, collabById, payBases, pjRates]); // eslint-disable-line react-hooks/exhaustive-deps
  const bQ2 = useMemo(() => applyBaseComponents(payQ2.data, { baseComps, etypeOf: etypeOfG, baseOf: cfgBaseOf, pj: pjRates }), [payQ2.data, baseComps, etypeOfG, collabById, payBases, pjRates]); // eslint-disable-line react-hooks/exhaustive-deps
  const taxItems = useMemo(() => ({ ...(settings.tax_items || {}), ...baseMaps.tax }), [settings.tax_items, baseMaps]);
  const classify = useMemo(() => makeClassifier(configById, taxItems), [configById, taxItems]);
  const disabledItems = useMemo(() => ({ ...(settings.disabled_items || {}), ...baseMaps.disabled }), [settings.disabled_items, baseMaps]);
  const quinzenaItems = useMemo(() => ({ ...(settings.quinzena_items || {}), ...baseMaps.quinzena }), [settings.quinzena_items, baseMaps]);
  const inQuinzena = (it) => isMensal || !quinzenaItems[it.id] || Number(quinzenaItems[it.id]) === Number(period);
  const autoBonusItems = useMemo(() => ({ ...(settings.auto_bonus_items || {}), ...baseMaps.auto }), [settings.auto_bonus_items, baseMaps]);
  const baseFlags = useMemo(() => ({ ...(settings.base_salary_items || {}), ...baseMaps.baseSalary }), [settings.base_salary_items, baseMaps]);
  const isBaseSal = (it, items) => isBaseSalaryItem(it, { flags: baseFlags, anyFlag: hasBaseFlag(items, baseFlags), label: configById[it.id]?.label, isSalary: classify(it) === "salary" });
  const mins = useMemo(() => ({ ...(cfg.data || {}), mestre_min: cfg.data?.mestre_min ?? settings.mestre_min }), [cfg.data, settings.mestre_min]);
  const liveMinsFor = useMinsFor();
  const contractOf = useContractTypes();
  const baseCfgs = useBaseConfigs().data;
  const minsFor = useMemo(() => (snap ? minsForWith(snap.collabs?.collaborators, snap.baseConfigs) : liveMinsFor), [snap, liveMinsFor]);
  const tiers = tiersData.data?.tiers || [];
  const deliveriesByUser = useMemo(() => {
    const m = {};
    (reports.data?.rows || []).forEach((r) => { m[r.id] = r.deliveries || 0; });
    return m;
  }, [reports.data]);
  // Benefícios reais por colaborador (mesma fonte da página Benefícios): dias com fuel/meal/commute + produtividade por quinzena.
  const benDaysById = useMemo(() => {
    const m = {};
    (benefitsReport.data?.drivers || []).forEach((d) => { m[d.id] = d.days || []; });
    return m;
  }, [benefitsReport.data]);
  const q1By = useMemo(() => { const m = {}; (reports1.data?.rows || []).forEach((r) => { m[r.id] = r.deliveries || 0; }); return m; }, [reports1.data]);
  const q2By = useMemo(() => { const m = {}; (reports2.data?.rows || []).forEach((r) => { m[r.id] = r.deliveries || 0; }); return m; }, [reports2.data]);
  const prodOf = (id) => {
    let b = 0;
    if (period === 1 || isMensal) b += computeAutoBonus(q1By[id] || 0, minsFor(id, mins), tiers, contractOf(id)).value;
    if (period === 2 || isMensal) b += computeAutoBonus(q2By[id] || 0, minsFor(id, mins), tiers, contractOf(id)).value;
    return b;
  };
  const inPeriod = (dt) => { const dd = Number(String(dt).slice(8, 10)); return isMensal ? true : (period === 1 ? dd <= 15 : dd >= 16); };
  const benSum = (id, k) => (benDaysById[id] || []).reduce((a, dy) => (inPeriod(dy.date) ? a + (dy[k] || 0) : a), 0);
  const benefitOf = (id) => benSum(id, "fuel") + benSum(id, "meal") + benSum(id, "commute") + prodOf(id);
  // Só componentes habilitados: switch "Ativo na folha" do painel + flag active do upstream.
  const effItems = (items) => (items || []).filter((it) => !disabledItems[it.id] && it.active !== false && configById[it.id]?.active !== false && inQuinzena(it));
  const isAuto = (it) => autoBonusItems[it.id] || it.source === "bonus_auto";
  // Resolve os itens de uma linha: aplica o bônus automático (por entregas da quinzena) aos itens "Bônus (auto)"
  // e o salário base individual (override) ao primeiro item fixo de salário.
  const resolveRaw = (l) => {
    let baseDone = false;
    const eff = effItems(l.items);
    return eff.map((it) => {
      if (isAuto(it)) {
        if (it._autoValue != null) return { ...it, value: it._autoValue, kind: "benefit", is_discount: false, label: it._autoLabel };
        const { level, value } = computeAutoBonus(deliveriesByUser[l.user_id] || 0, minsFor(l.user_id, mins), tiers, contractOf(l.user_id));
        return { ...it, value, kind: "benefit", is_discount: false, label: `${it.label || "Bônus"} — ${level || "sem nível"}` };
      }
      if (!baseDone && isBaseSal(it, eff)) {
        baseDone = true;
        const n = it._n || 1;
        const global = (configById[it.id]?.value ?? it.value / n) * n;
        const bv = baseSalOf(l.user_id, it.id);
        const base = bv != null ? bv * n : global;
        const ov = salOverride[l.user_id] != null ? salOverride[l.user_id] * n : null;
        if (ov != null) return { ...it, value: ov, isBaseSalary: true, override: true, salLevel: "individual", catValue: base };
        return bv != null ? { ...it, value: base, isBaseSalary: true, salLevel: "base", catValue: base } : { ...it, value: global, isBaseSalary: true, salLevel: "global", catValue: base };
      }
      return it;
    });
  };
  // Valor manual do componente: na quinzena substitui; na Mensal soma cada quinzena (manual ou calculada).
  const applyOv = (uid, x) => {
    if (!x.id) return x;
    if (!isMensal) { const v = ovs[ovKey(period, uid, x.id)]; return v != null ? { ...x, value: Number(v), manual: true } : x; }
    const o = [1, 2].map((q) => ovs[ovKey(q, uid, x.id)]);
    if (o.every((v) => v == null)) return x;
    const perQ = (q) => (isAuto(x) ? (x._autoQ?.[q] ?? 0) : x.isBaseSalary ? (q in (x._q || {}) ? x.value / (x._n || 1) : 0) : (x._q?.[q] ?? 0));
    return { ...x, value: [1, 2].reduce((a, q, i) => a + (o[i] != null ? Number(o[i]) : perQ(q)), 0), manual: true };
  };
  const resolveItems = (l) => resolveRaw(l).map((x) => applyOv(l.user_id, x));
  const saveOverride = async (uid, itemId, value) => {
    if (value !== null && (value === "" || Number.isNaN(Number(value)) || Number(value) < 0)) { toast.error("Informe um valor válido"); return; }
    try {
      await panelApi.put("/panel/comp-override", { month, quinzena: period, user_id: uid, item_id: itemId, value: value === null ? null : Number(value) });
      await queryClient.invalidateQueries({ queryKey: ["comp-overrides", month] });
      toast.success(value === null ? "Valor voltou ao padrão" : "Valor manual salvo para este colaborador");
    } catch (e) { toast.error(apiError(e)); }
  };
  const lineTotalOf = (l) => resolveItems(l).reduce((a, x) => { const c = classify(x); return a + ((c === "discount" || c === "tax") ? -(x.value || 0) : (x.value || 0)); }, 0) + indivNet(l.user_id);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);

  // Benefícios/descontos individuais por colaborador + quinzena (armazenados no backend do painel).
  const [indivLive, setIndiv] = useState([]);
  const indiv = snap?.indiv || indivLive;
  const [indivForm, setIndivForm] = useState(null); // { user_id, label, kind, value }
  const [indivBusy, setIndivBusy] = useState(false);
  const reloadIndiv = () => {
    const qs = isMensal ? [1, 2] : [period];
    Promise.all(qs.map((q) => panelApi.get("/panel/individual-benefits", { params: { month, quinzena: q } })))
      .then((res) => setIndiv(res.flatMap((r) => r.data?.benefits || [])))
      .catch(() => setIndiv([]));
  };
  useEffect(() => { reloadIndiv(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month, period, isMensal]);
  const indivByUser = useMemo(() => {
    const m = {};
    indiv.forEach((b) => { (m[b.user_id] = m[b.user_id] || []).push(b); });
    return m;
  }, [indiv]);
  const indivBenOf = (uid) => (indivByUser[uid] || []).filter((b) => b.kind !== "discount");
  const indivDiscOf = (uid) => (indivByUser[uid] || []).filter((b) => b.kind === "discount");
  const indivNet = (uid) => (indivByUser[uid] || []).reduce((a, b) => a + (b.kind === "discount" ? -(Number(b.value) || 0) : (Number(b.value) || 0)), 0);

  const addIndiv = async () => {
    if (!indivForm?.label.trim()) { toast.error("Informe a descrição do benefício"); return; }
    setIndivBusy(true);
    try {
      await panelApi.post("/panel/individual-benefits", {
        month, quinzena: period, user_id: indivForm.user_id,
        label: indivForm.label.trim(), kind: indivForm.kind, value: Number(indivForm.value) || 0,
      });
      toast.success("Benefício individual adicionado");
      setIndivForm(null);
      reloadIndiv();
    } catch (e) { toast.error(apiError(e)); } finally { setIndivBusy(false); }
  };
  const delIndiv = async (id) => {
    try { await panelApi.delete(`/panel/individual-benefits/${id}`); toast.success("Removido"); reloadIndiv(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const faltasByUser = useMemo(() => {
    const map = {};
    (faltas.data?.faltas || []).forEach((f) => {
      const amt = Number(f.discount_amount) || 0;
      if (f.month !== month || amt <= 0) return;
      (map[f.user_id] = map[f.user_id] || []).push(f);
    });
    return map;
  }, [faltas.data, month]);
  const faltaSum = (uid) => (faltasByUser[uid] || []).reduce((a, f) => a + (Number(f.discount_amount) || 0), 0);

  // Mensal = 1ª + 2ª quinzena (as que já foram geradas), cada uma respeitando o campo quinzena do componente.
  const data = useMemo(() => {
    if (!isMensal) return bMonthly;
    const qs = [[bQ1?.payroll, 1, q1By], [bQ2?.payroll, 2, q2By]].filter(([p]) => p?.lines?.length);
    if (!qs.length) return bMonthly;
    const users = {};
    qs.forEach(([p, q, dBy]) => p.lines.forEach((l) => {
      const u = users[l.user_id] || (users[l.user_id] = { ...l, items: [], _byId: {} });
      (l.items || []).forEach((it) => {
        if (quinzenaItems[it.id] && Number(quinzenaItems[it.id]) !== q) return;
        let x = u._byId[it.id];
        if (!x) { x = u._byId[it.id] = { ...it, value: 0, _n: 0, _q: {} }; u.items.push(x); }
        x.value += Number(it.value) || 0;
        x._q[q] = Number(it.value) || 0;
        x._n += 1;
        if (autoBonusItems[it.id] || it.source === "bonus_auto") {
          const b = computeAutoBonus(dBy[l.user_id] || 0, minsFor(l.user_id, mins), tiers, contractOf(l.user_id));
          x._autoValue = (x._autoValue || 0) + b.value;
          x._autoQ = { ...(x._autoQ || {}), [q]: b.value };
          x._autoLabel = `${x._autoLabel ? `${x._autoLabel} / ` : `${it.label || "Bônus"} — `}${q}ª: ${b.level || "sem nível"}`;
        }
      });
    }));
    const base = bMonthly?.payroll || qs[0][0];
    return { ...(bMonthly || {}), payroll: { ...base, lines: Object.values(users).map(({ _byId, ...l }) => l) } };
  }, [isMensal, bMonthly, bQ1, bQ2, q1By, q2By, quinzenaItems, autoBonusItems, minsFor, mins, tiers, contractOf]);
  const payroll = data?.payroll || null;
  const pid = payroll?.id;
  const upstreamClosed = payroll?.status === "closed";
  const closed = !!lock;
  const companyTotal = (payroll?.lines || []).reduce((a, l) => a + lineTotalOf(l), 0);

  // PJ: colaboradores e valor por entrega vêm das seções ciclista_pj / motociclista_pj de /admin/payroll-settings.
  const pjSectionOf = {}; const pjOverride = {};
  PJ_SECTIONS.forEach((s) => (pjSettings.data?.sections?.[s]?.drivers || []).forEach((d) => { pjSectionOf[d.id] = s; pjOverride[d.id] = d.pj_rate_override; }));
  const pjSec = (id) => {
    const s = pjSectionOf[id], sec = pjSettings.data?.sections?.[s] || {};
    const b = s ? cfgBaseOf(id) : null;
    const comp = b ? baseComps[b]?.[s] : null;
    return comp ? { ...sec, items: baseItemsFor(b, s, comp), valor_por_entrega: comp.pj_rate ?? sec.valor_por_entrega } : sec;
  };
  const pjRateOf = (id) => (pjOverride[id] != null ? Number(pjOverride[id]) : (Number(pjSec(id).valor_por_entrega) || 0));
  // Detalhamento PJ: benefícios coletivos (auto do período + itens fixos da seção PJ), descontos e impostos.
  // Componentes individuais com a mesma descrição de um coletivo o sobrepõem (o coletivo é ocultado).
  const norm = (s) => String(s || "").trim().toLowerCase();
  const pjBreakdown = (id) => {
    const indivL = new Set((indivByUser[id] || []).map((b) => norm(b.label)));
    const activeSrc = new Set(effItems(pjSec(id).items).map((it) => (isAuto(it) ? "bonus" : it.source)));
    const ben = [
      { id: "auto-fuel", src: "fuel", label: "Combustível", value: benSum(id, "fuel") },
      { id: "auto-meal", src: "meal", label: "Vale alimentação", value: benSum(id, "meal") },
      { id: "auto-commute", src: "commute", label: "Auxílio deslocamento", value: benSum(id, "commute") },
      { id: "auto-prod", src: "bonus", label: "Produtividade", value: prodOf(id) },
    ].filter((x) => x.value && activeSrc.has(x.src));
    const desc = []; const imp = [];
    effItems(pjSec(id).items).forEach((it) => {
      if (LOCKED_SOURCES.includes(it.source) || isAuto(it)) return;
      const c = classify(it); const x = { id: it.id, label: it.label, value: Number(it.value) || 0 };
      (c === "tax" ? imp : c === "discount" ? desc : ben).push(x);
    });
    const keep = (a) => a.filter((x) => !indivL.has(norm(x.label)));
    const sum = (a) => a.reduce((s, x) => s + (Number(x.value) || 0), 0);
    const b = keep(ben), d = keep(desc), t = keep(imp);
    const iB = indivBenOf(id), iD = indivDiscOf(id);
    return { ben: b, desc: d, imp: t, sBen: sum(b) + sum(iB), sDesc: sum(d) + sum(iD), sImp: sum(t), iB, iD };
  };
  const indivFormEl = (uid) => indivForm && indivForm.user_id === uid && (
    <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg border border-emerald-900/40 bg-slate-950 p-3 sm:grid-cols-12" data-testid={`indiv-form-${uid}`}>
      <Input value={indivForm.label} onChange={(e) => setIndivForm((f) => ({ ...f, label: e.target.value }))} placeholder="Descrição do benefício" data-testid="indiv-form-label" className="border-slate-700 bg-slate-950 text-slate-100 sm:col-span-5" />
      <div className="sm:col-span-3">
        <Select value={indivForm.kind} onValueChange={(v) => setIndivForm((f) => ({ ...f, kind: v }))}>
          <SelectTrigger data-testid="indiv-form-kind" className="border-slate-700 bg-slate-950 text-slate-100"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
            <SelectItem value="benefit">Benefício</SelectItem>
            <SelectItem value="discount">Desconto</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Input type="number" step="0.01" min="0" value={indivForm.value} onChange={(e) => setIndivForm((f) => ({ ...f, value: e.target.value }))} placeholder="Valor" data-testid="indiv-form-value" className="border-slate-700 bg-slate-950 text-slate-100 sm:col-span-2" />
      <div className="flex gap-1.5 sm:col-span-2">
        <Button size="sm" className="h-9 flex-1 bg-emerald-600 text-white hover:bg-emerald-700" disabled={indivBusy} onClick={addIndiv} data-testid="indiv-form-save">Salvar</Button>
        <Button size="sm" variant="outline" className="h-9 border-slate-700 text-slate-300" onClick={() => setIndivForm(null)} data-testid="indiv-form-cancel">Cancelar</Button>
      </div>
    </div>
  );
  const itemCard = (it, color, neg, tag) => (
    <div key={it.id} className={`flex items-center justify-between rounded-lg border ${tag ? (neg ? "border-rose-900/40" : "border-emerald-900/40") : "border-slate-800"} bg-slate-900 px-3 py-2`} data-testid={tag ? `indiv-${neg ? "disc" : "ben"}-${it.id}` : undefined}>
      <span className="truncate text-sm text-slate-300">{it.label}{tag && <span className={`ml-1 rounded px-1 text-[10px] ${neg ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>individual</span>}</span>
      <span className="flex items-center gap-2">
        <span className={`font-mono text-sm ${color}`}>{neg ? "- " : ""}{brl(it.value)}</span>
        {tag && !isMensal && !closed && <button onClick={() => delIndiv(it.id)} className="text-slate-500 hover:text-rose-400" data-testid={`indiv-del-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
      </span>
    </div>
  );
  const cltLines = (payroll?.lines || []).filter((l) => !pjSectionOf[l.user_id]);
  const pjLines = (payroll?.lines || []).filter((l) => pjSectionOf[l.user_id]);

  // Detalhamento do colaborador (modal dos Indicadores do período), com os mesmos cálculos das tabelas CLT/PJ.
  const [detailId, setDetailId] = useState(null);
  const detailOf = (id) => {
    const r = (reports.data?.rows || []).find((x) => x.id === id);
    const l = (payroll?.lines || []).find((x) => x.user_id === id);
    if (!r && !l) return null;
    // Quinzena do componente (configuração de folha) que gera cada categoria; sem marcação = Ambas.
    const comps = pjSectionOf[id] ? (pjSec(id).items || []) : (l?.items || []);
    const qOf = (srcs) => { const c = comps.find((it) => srcs.includes(it.source) && quinzenaItems[it.id]); return c ? Number(quinzenaItems[c.id]) : null; };
    const daySum = (k, q) => (benDaysById[id] || []).reduce((a, dy) => { const dd = Number(String(dy.date).slice(8, 10)); return (q === 1 ? dd <= 15 : dd >= 16) ? a + (dy[k] || 0) : a; }, 0);
    const prodQ = (q) => computeAutoBonus((q === 1 ? q1By : q2By)[id] || 0, minsFor(id, mins), tiers, contractOf(id)).value;
    const catVal = (srcs, full, byQ) => { const q = qOf(srcs); if (!q) return full; if (isMensal) return byQ(q); return Number(period) === q ? full : null; };
    const base = {
      id, name: r?.name || l.name, deliveries: r?.deliveries || 0, km: r?.km || 0,
      ben: {
        fuel: catVal(["fuel"], benSum(id, "fuel"), (q) => daySum("fuel", q)),
        meal: catVal(["meal"], benSum(id, "meal"), (q) => daySum("meal", q)),
        commute: catVal(["commute"], benSum(id, "commute"), (q) => daySum("commute", q)),
        prod: contractOf(id) === "pj" ? null : catVal(["bonus", "bonus_auto"], prodOf(id), prodQ),
      },
    };
    if (!l) return base;
    const fs = faltaSum(id);
    const mk = (key, title, tone, items) => ({ key, title, tone, items, total: items.reduce((a, x) => a + (Number(x.value) || 0), 0) });
    const tagI = (a) => a.map((b) => ({ label: b.label, value: b.value, tag: "individual" }));
    const faltaIt = fs ? [{ label: "Descontos por falta", value: fs }] : [];
    if (pjSectionOf[id]) {
      const g = pjBreakdown(id);
      const del = deliveriesByUser[id] || 0;
      const sec = [
        mk("prov", "Proventos", "pos", [{ label: `Entregas (${num(del)} × ${brl(pjRateOf(id))})`, value: del * pjRateOf(id) }, ...g.ben, ...tagI(g.iB)]),
        mk("desc", "Descontos", "neg", [...g.desc, ...tagI(g.iD), ...faltaIt]),
        mk("imp", "Impostos", "tax", g.imp),
      ];
      return { ...base, type: "PJ", sections: sec, net: sec[0].total - sec[1].total - sec[2].total };
    }
    const g = groupItems(resolveItems(l), classify);
    const sec = [
      mk("prov", "Proventos", "pos", [...g.salario, ...g.beneficios, ...tagI(indivBenOf(id))]),
      mk("desc", "Descontos", "neg", [...g.descontos, ...tagI(indivDiscOf(id)), ...faltaIt]),
      mk("imp", "Impostos", "tax", g.impostos),
    ];
    return { ...base, type: `CLT · ${l.employment_type || l.vehicle_type || "—"}`, sections: sec, net: lineTotalOf(l) - fs };
  };

  const run = async (fn, key) => {
    setBusy(key);
    try { await fn(); await refetch(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const generate = (regen) => run(async () => { await api.post("/admin/payroll/generate", { month, regenerate: !!regen, ...quinzenaParam }); toast.success(regen ? "Folha regerada" : "Folha gerada"); }, "gen");
  const isQ = period === 1 || period === 2;
  const closeP = () => run(async () => {
    const closeUp = isQ && pid && !upstreamClosed;
    const snapshot = {
      monthly: liveMonthly, payQ1: payQ1Live.data, payQ2: payQ2Live.data,
      ...Object.fromEntries(Object.entries(live).map(([k, v]) => [k, v.data])),
      settings: liveSettings, indiv: indivLive, compOverrides: ovQ.data || {}, baseConfigs: baseCfgs || {}, payBases: livePayBases || {}, baseSalaries: liveBaseSalaries || {}, baseComponents: liveBaseComps || {},
      upstreamClosedByPanel: closeUp ? pid : null,
    };
    if (closeUp) await api.post(`/admin/payroll/${pid}/close`);
    try {
      await panelApi.put("/panel/payroll-lock", { ...lockParams, snapshot_json: JSON.stringify(snapshot) });
    } catch (e) {
      if (closeUp) await api.post(`/admin/payroll/${pid}/reopen`).catch(() => {});
      throw e;
    }
    await lockQ.refetch();
    queryClient.invalidateQueries({ queryKey: ["payroll-lock"] });
    toast.success(lockParams.base ? "Folha fechada — valores congelados" : "Folha fechada em todas as bases — valores congelados");
  }, "close");
  const reopenP = () => run(async () => {
    const upId = snap?.upstreamClosedByPanel;
    if (upId) await api.post(`/admin/payroll/${upId}/reopen`);
    await panelApi.delete("/panel/payroll-lock", { params: { ...lockParams, base: inheritedLock ? "" : lockParams.base } });
    await lockQ.refetch();
    queryClient.invalidateQueries({ queryKey: ["payroll-lock"] });
    toast.success(inheritedLock ? "Folha reaberta em todas as bases" : "Folha reaberta");
  }, "reopen");

  const buildFolhaPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    addPdfLogo(doc, logo, { top: 10 });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40;
    let y = M;
    const line = (label, val, o = {}) => {
      if (y > H - 50) { doc.addPage(); y = M; }
      doc.setFontSize(o.size || 10);
      doc.setFont("helvetica", o.bold ? "bold" : "normal");
      doc.setTextColor(...(o.color || [40, 40, 40]));
      doc.text(String(label), o.x != null ? o.x : M, y);
      if (val != null) doc.text(String(val), W - M, y, { align: "right" });
      y += o.gap || 15;
    };
    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(20, 20, 20);
    doc.text("Central do Courier - Folha de Pagamento", M, y); y += 20;
    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(90, 90, 90);
    doc.text(`Periodo: ${month} - ${periodLabel}    Status: ${closed ? "Fechada" : "Aberta"}`, M, y); y += 8;
    doc.setDrawColor(220); doc.line(M, y, W - M, y); y += 18;

    (payroll.lines || []).forEach((l) => {
      const g = groupItems(resolveItems(l), classify);
      if (y > H - 130) { doc.addPage(); y = M; }
      line(`${l.name}  (${l.employment_type || l.vehicle_type || "-"})`, brl(lineTotalOf(l)), { bold: true, size: 11, gap: 16 });
      line("Salario", brl(g.sSal), { bold: true, size: 9, color: [71, 85, 105] });
      g.salario.forEach((it) => line(`    ${it.label}`, brl(it.value), { size: 9 }));
      if (g.salario.length === 0) line("    (sem itens)", "", { size: 9, color: [150, 150, 150] });
      const ibs = indivBenOf(l.user_id);
      const idc = indivDiscOf(l.user_id);
      line("Beneficios", brl(g.sBen + ibs.reduce((a, b) => a + (Number(b.value) || 0), 0)), { bold: true, size: 9, color: [16, 122, 87] });
      g.beneficios.forEach((it) => line(`    ${it.label}`, brl(it.value), { size: 9 }));
      ibs.forEach((b) => line(`    ${b.label} (individual)`, brl(b.value), { size: 9 }));
      if (g.beneficios.length === 0 && ibs.length === 0) line("    (sem itens)", "", { size: 9, color: [150, 150, 150] });
      if (g.descontos.length || idc.length) {
        line("Descontos", `- ${brl(g.sDesc + idc.reduce((a, b) => a + (Number(b.value) || 0), 0))}`, { bold: true, size: 9, color: [190, 60, 60] });
        g.descontos.forEach((it) => line(`    ${it.label}`, `- ${brl(it.value)}`, { size: 9 }));
        idc.forEach((b) => line(`    ${b.label} (individual)`, `- ${brl(b.value)}`, { size: 9 }));
      }
      if (g.impostos.length) {
        line("Impostos", `- ${brl(g.sImp)}`, { bold: true, size: 9, color: [180, 130, 40] });
        g.impostos.forEach((it) => line(`    ${it.label}`, `- ${brl(it.value)}`, { size: 9 }));
      }
      const fls = faltasByUser[l.user_id] || [];
      if (fls.length) {
        line("Descontos por falta", `- ${brl(faltaSum(l.user_id))}`, { bold: true, size: 9, color: [190, 60, 60] });
        fls.forEach((f) => line(`    Falta ${new Date(f.date).toLocaleDateString("pt-BR")} (${f.status === "approved" ? "aprovada" : "reprovada"})`, `- ${brl(f.discount_amount)}`, { size: 9 }));
        line("Total com faltas", brl(lineTotalOf(l) - faltaSum(l.user_id)), { bold: true, size: 9, color: [40, 40, 40] });
      }
      y += 4; doc.setDrawColor(235); doc.line(M, y, W - M, y); y += 14;
    });

    if (y > H - 60) { doc.addPage(); y = M; }
    doc.setDrawColor(140); doc.line(M, y, W - M, y); y += 20;
    line("TOTAL GERAL DA EMPRESA", brl(companyTotal), { bold: true, size: 12 });
    const rep = reports.data;
    if (rep && (rep.rows || []).length) {
      y += 12; if (y > H - 90) { doc.addPage(); y = M; }
      line(`Indicadores - ${periodLabel}  (${rep.label || ""})`, null, { bold: true, size: 11 });
      line("Colaborador", "Entregas / KM / Beneficios", { bold: true, size: 9, color: [71, 85, 105] });
      rep.rows.forEach((r) => line(`    ${r.name}`, `${r.deliveries || 0}  /  ${km(r.km)}  /  ${brl(benefitOf(r.id))}`, { size: 9 }));
    }
    return doc;
  };
  const pdfName = `folha_${month}_${isMensal ? "mensal" : "q" + period}.pdf`;
  const downloadPdf = () => {
    buildFolhaPdf().save(pdfName);
    toast.success("PDF gerado");
  };
  const allList = () => {
    const ids = [...new Set([...(payroll?.lines || []).map((l) => l.user_id), ...(reports.data?.rows || []).map((r) => r.id)])];
    return ids.map(detailOf).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  };
  const allPdfName = `detalhamento-todos-${month}-${isMensal ? "mensal" : `q${period}`}.pdf`;

  const xlsxPayload = (onlyId) => {
    const dl = {};
    (reports.data?.rows || []).forEach((r) => { dl[r.id] = r.deliveries || 0; });
    const rows = (payroll?.lines || [])
      .filter((l) => !pjSectionOf[l.user_id] && String(l.contract_type || "").toLowerCase() !== "pj" && (!onlyId || l.user_id === onlyId))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "pt-BR"))
      .map((l) => toXlsxRow({ name: l.name, vehicle: vehicleOf(l), contract: "CLT", deliveries: dl[l.user_id] ?? null }, [
        ...resolveItems(l).map((it) => ({ label: configById[it.id]?.label || it.label, value: it.value, source: configById[it.id]?.source || it.source, cls: classify(it), isBaseSalary: it.isBaseSalary, auto: isAuto(it) })),
        ...indivBenOf(l.user_id).map((b) => ({ label: b.label, value: b.value, cls: "benefit" })),
        ...indivDiscOf(l.user_id).map((b) => ({ label: b.label, value: b.value, cls: "discount" })),
        { label: "Faltas", value: faltaSum(l.user_id), cls: "discount" },
      ]));
    const baseLabel = allBases.find((b) => b.id === curBaseId)?.name || "Todas as bases";
    return { payload: { title_kind: isMensal ? "MENSAL" : "QUINZENAL", period_text: periodText(month, period, baseLabel), rows }, baseLabel };
  };
  const xlsxName = `folha_${month}_${isMensal ? "mensal" : "q" + period}.xlsx`;
  const exportXlsx = () => downloadPayrollXlsx(xlsxPayload().payload, xlsxName);
  const shareWhats = (kind) => {
    const { payload, baseLabel } = xlsxPayload();
    const text = `Folha de pagamento ${month} · ${periodLabel} · ${baseLabel}. Total da empresa: ${brl(companyTotal)}.`;
    if (kind === "excel") { shareXlsxWhatsApp(payload, xlsxName, `${text} Segue a planilha da folha.`); return; }
    const list = allList();
    sharePayrollWhatsApp({
      pdfs: [{ doc: buildFolhaPdf(), name: pdfName }, list.length ? { doc: buildAllPdf(list, `${month} · ${periodLabel}`, logo), name: allPdfName } : null],
      text: `${text} Segue a folha e o detalhamento em PDF.`,
    });
  };
  const shareOneExcel = (d, text, phone) => {
    const slug = String(d.name).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    shareXlsxWhatsApp(xlsxPayload(d.id).payload, `folha_${month}_${isMensal ? "mensal" : "q" + period}_${slug}.xlsx`, text, phone);
  };

  const periodButtons = (
    <div className="flex flex-wrap overflow-hidden rounded-lg border border-slate-700">
      {[{ v: "mensal", l: "Mensal" }, { v: 1, l: "1ª Quinzena" }, { v: 2, l: "2ª Quinzena" }, { v: "13-1", l: "1ª Parcela Décimo Terceiro" }, { v: "13-2", l: "2ª Parcela Décimo Terceiro" }].map((p) => (
        <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`folha-period-${p.v}`}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${period === p.v ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`}>
          {p.l}
        </button>
      ))}
    </div>
  );

  if (is13) {
    // 13º: só CLT. Base = soma mensal dos proventos da config (exceto benefícios combustível/VR/deslocamento/produtividade).
    const people = {};
    const l1 = bQ1?.payroll?.lines, l2 = bQ2?.payroll?.lines;
    const qSrc = [[1, l1?.length ? l1 : l2], [2, l2?.length ? l2 : l1]].filter(([, ls]) => ls?.length);
    const BEN_SOURCES = ["fuel", "meal", "commute", "bonus", "bonus_auto"];
    const in13Base = (it, q) => {
      const c = configById[it.id];
      if (disabledItems[it.id] || it.active === false || c?.active === false) return false;
      if (quinzenaItems[it.id] && Number(quinzenaItems[it.id]) !== q) return false;
      if (BEN_SOURCES.includes(c?.source || it.source) || isAuto(it)) return false;
      const k = classify(it);
      return k === "salary" || k === "benefit";
    };
    qSrc.forEach(([q, lines]) => lines.forEach((l) => {
      if (pjSectionOf[l.user_id] || String(l.contract_type || "").toLowerCase() === "pj") return;
      const cat = l.role === "funcionario" ? "Administrativo CLT" : (l.vehicle_type === "motociclista" ? "Motoboy CLT" : "Ciclista CLT");
      const p = people[l.user_id] || (people[l.user_id] = { id: l.user_id, name: l.name, category: cat, salary: 0, parts: {} });
      let baseDone = false;
      (l.items || []).filter((it) => in13Base(it, q)).forEach((it) => {
        let v = configById[it.id]?.value ?? (Number(it.value) || 0);
        if (!baseDone && isBaseSal(it, l.items)) { baseDone = true; const bv = baseSalOf(l.user_id, it.id); if (bv != null) v = bv; if (salOverride[l.user_id] != null) v = salOverride[l.user_id]; }
        const lbl = configById[it.id]?.label || it.label || "Provento";
        p.salary += Number(v) || 0;
        p.parts[lbl] = (p.parts[lbl] || 0) + (Number(v) || 0);
      });
    }));
    const list = Object.values(people).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="folha-month" className="w-44 border-slate-700 bg-slate-900 text-slate-100" />
          {periodButtons}
        </div>
        {payQ1.loading && payQ2.loading ? <Loading /> : <ThirteenthPanel year={Number(month.slice(0, 4))} parcela={period === "13-1" ? 1 : 2} people={list} />}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="folha-month"
          className="w-44 border-slate-700 bg-slate-900 text-slate-100" />
        {periodButtons}
        <span className="ml-auto" />
        {payroll && (
          <>
            <Button variant="outline" className="border-slate-700 text-slate-300" data-testid="folha-pdf-all" disabled={!payroll && !(reports.data?.rows || []).length}
              onClick={() => {
                const list = allList();
                if (!list.length) { toast.error("Nenhum colaborador no período"); return; }
                buildAllPdf(list, `${month} · ${periodLabel}`, logo).save(allPdfName);
              }}>
              <FileText className="h-4 w-4" /> PDF de todos
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={downloadPdf} data-testid="folha-pdf">
              <FileText className="h-4 w-4" /> Baixar PDF
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={exportXlsx} data-testid="folha-xlsx">
              <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
            </Button>
            <WhatsAppMenu testId="folha-whatsapp" onPdf={() => shareWhats("pdf")} onExcel={() => shareWhats("excel")} />
            {!isMensal && (
              <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => generate(true)} disabled={busy === "gen" || closed || upstreamClosed} data-testid="folha-regenerate">
                <RefreshCw className="h-4 w-4" /> Regerar
              </Button>
            )}
            {closed ? (
              <Button className="bg-amber-600 text-white hover:bg-amber-700" onClick={reopenP} disabled={busy === "reopen"} data-testid="folha-reopen">
                <Unlock className="h-4 w-4" /> {inheritedLock ? "Reabrir (todas as bases)" : "Reabrir folha"}
              </Button>
            ) : (
              <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={closeP} disabled={busy === "close"} data-testid="folha-close">
                <Lock className="h-4 w-4" /> Fechar folha
              </Button>
            )}
          </>
        )}
      </div>

      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : !payroll ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-slate-700 bg-slate-900 py-16 text-center">
          <Wallet className="h-10 w-10 text-slate-600" />
          <p className="text-slate-200">Nenhuma folha gerada para {month}</p>
          <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={() => generate(false)} disabled={busy === "gen"} data-testid="folha-generate">
            <Sparkles className="h-4 w-4" /> Gerar folha de {month}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-900 p-5">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Total da empresa</p>
              <p className="font-mono text-3xl font-extrabold text-white" data-testid="folha-company-total">{brl(companyTotal)}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-sm font-semibold ${closed ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="folha-status">
              {closed ? `Fechada${inheritedLock ? " em Todas as bases" : ""}${lock?.closed_at ? ` em ${new Date(lock.closed_at).toLocaleString("pt-BR")}` : ""} · valores congelados${snap && !snap.payBases ? " (fechada antes da base de pagamento — reabra e feche para aplicar)" : ""}` : "Aberta"}
            </span>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="folha-quinzena">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-medium text-slate-200">Indicadores do período</h3>
                <p className="text-xs text-slate-500">Entregas, KM e benefícios reais apurados no período selecionado. No modo Mensal somamos a 1ª e a 2ª quinzena (somente leitura). A folha de salário/benefícios permanece mensal.</p>
              </div>
              <span className="rounded bg-orange-500/15 px-2.5 py-1 text-sm font-semibold text-orange-300">{reports.data?.label || periodLabel}</span>
            </div>
            {reports.loading ? <Loading /> : reports.error ? <ErrorState error={reports.error} onRetry={reports.refetch} /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="folha-quinzena-table">
                  <thead>
                    <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 font-semibold">Colaborador</th>
                      <th className="px-3 py-2 font-semibold">Entregas</th>
                      <th className="px-3 py-2 font-semibold">KM</th>
                      <th className="px-3 py-2 font-semibold">Benefícios</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {(reports.data?.rows || []).map((r) => (
                      <tr key={r.id} className="hover:bg-slate-800/40">
                        <td className="px-3 py-2 font-medium text-slate-100">
                          <button type="button" onClick={() => setDetailId(r.id)} data-testid={`folha-ind-name-${r.id}`}
                            className="text-left underline-offset-4 transition-colors hover:text-orange-300 hover:underline">{r.name}</button>
                        </td>
                        <td className="px-3 py-2 font-mono text-orange-300">{num(r.deliveries)}</td>
                        <td className="px-3 py-2 font-mono text-slate-300">{km(r.km)}</td>
                        <td className="px-3 py-2 font-mono text-emerald-300" data-testid={`folha-benef-${r.id}`}>{brl(benefitOf(r.id))}</td>
                      </tr>
                    ))}
                    {(reports.data?.rows || []).length === 0 && <tr><td colSpan={4}><Empty label="Sem dados na quinzena" /></td></tr>}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-800">
                      <td className="px-3 py-2 font-semibold text-slate-200">Total</td>
                      <td className="px-3 py-2 font-mono font-bold text-orange-300">{num((reports.data?.rows || []).reduce((a, r) => a + (r.deliveries || 0), 0))}</td>
                      <td className="px-3 py-2 font-mono text-slate-300">{km((reports.data?.rows || []).reduce((a, r) => a + (r.km || 0), 0))}</td>
                      <td className="px-3 py-2 font-mono font-bold text-emerald-300">{brl((reports.data?.rows || []).reduce((a, r) => a + benefitOf(r.id), 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
          <CollaboratorDetailDialog detail={detailId ? detailOf(detailId) : null} periodLabel={`${month} · ${periodLabel}`} onClose={() => setDetailId(null)} onExcel={shareOneExcel}
            editable={gestor && !closed && !isMensal} onEditItem={(itemId, v) => saveOverride(detailId, itemId, v)} />
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-300" data-testid="folha-clt-heading">CLT</h3>
          <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
            <table className="w-full text-sm" data-testid="folha-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Colaborador</th>
                  <th className="px-4 py-3 font-semibold">Tipo</th>
                  <th className="px-4 py-3 font-semibold">Proventos</th>
                  <th className="px-4 py-3 font-semibold">Descontos</th>
                  <th className="px-4 py-3 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {cltLines.map((l) => {
                  const eff = resolveItems(l);
                  const prov = eff.filter((x) => { const c = classify(x); return c === "salary" || c === "benefit"; }).reduce((a, x) => a + (x.value || 0), 0) + indivBenOf(l.user_id).reduce((a, b) => a + (Number(b.value) || 0), 0);
                  const desc = eff.filter((x) => { const c = classify(x); return c === "discount" || c === "tax"; }).reduce((a, x) => a + (x.value || 0), 0) + indivDiscOf(l.user_id).reduce((a, b) => a + (Number(b.value) || 0), 0);
                  const lineTotal = lineTotalOf(l);
                  const open = expanded === l.user_id;
                  return (
                    <Fragment key={l.user_id}>
                      <tr className="cursor-pointer hover:bg-slate-800/40" onClick={() => setExpanded(open ? null : l.user_id)} data-testid={`folha-row-${l.user_id}`}>
                        <td className="px-4 py-3 font-medium text-slate-100">
                          <button type="button" onClick={(e) => { e.stopPropagation(); setDetailId(l.user_id); }} data-testid={`folha-name-${l.user_id}`}
                            className="text-left underline-offset-4 transition-colors hover:text-orange-300 hover:underline">{l.name}</button>
                        </td>
                        <td className="px-4 py-3 capitalize text-slate-400">{l.employment_type || l.vehicle_type || "—"}</td>
                        <td className="px-4 py-3 font-mono text-emerald-300">{brl(prov)}</td>
                        <td className="px-4 py-3 font-mono text-rose-300">{desc ? `- ${brl(desc)}` : brl(0)}</td>
                        <td className="px-4 py-3 font-mono font-semibold text-slate-100">{brl(lineTotal)}</td>
                      </tr>
                      {open && (
                        <tr key={`${l.user_id}-d`} className="bg-slate-950/50">
                          <td colSpan={5} className="px-4 py-3">
                            {(() => { const g = groupItems(eff, classify); return (
                              <div className="space-y-3" data-testid={`folha-detail-${l.user_id}`}>
                                <div>
                                  <div className="mb-1.5 flex items-center justify-between">
                                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Salário</span>
                                    <span className="font-mono text-sm font-semibold text-slate-200" data-testid={`folha-sal-${l.user_id}`}>{brl(g.sSal)}</span>
                                  </div>
                                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                    {g.salario.map((it) => it.isBaseSalary ? (
                                      <div key={it.id} className={`rounded-lg border px-3 py-2 ${it.override ? "border-sky-500/40 bg-sky-500/5" : "border-slate-800 bg-slate-900"}`} data-testid={`folha-basesal-${l.user_id}`}>
                                        {salEdit?.uid === l.user_id ? (
                                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                            <span className="mr-auto text-sm text-slate-300">{it.label}</span>
                                            <Input type="number" step="0.01" min="0" autoFocus value={salEdit.value}
                                              onChange={(e) => setSalEdit({ ...salEdit, value: e.target.value })}
                                              onKeyDown={(e) => { if (e.key === "Enter") saveSalary(l.user_id, salEdit.value); if (e.key === "Escape") setSalEdit(null); }}
                                              data-testid={`folha-basesal-input-${l.user_id}`} className="h-8 w-28 border-slate-700 bg-slate-950 font-mono text-slate-100" />
                                            <button disabled={salBusy} onClick={() => saveSalary(l.user_id, salEdit.value)} className="rounded p-1 text-emerald-400 hover:bg-slate-800" data-testid={`folha-basesal-confirm-${l.user_id}`}><Check className="h-4 w-4" /></button>
                                            <button onClick={() => setSalEdit(null)} className="rounded p-1 text-slate-400 hover:bg-slate-800" data-testid={`folha-basesal-cancel-${l.user_id}`}><X className="h-4 w-4" /></button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="flex items-center gap-1.5 text-sm text-slate-300">
                                              {it.label}
                                              {it.override && <span className="flex items-center gap-0.5 rounded bg-sky-500/15 px-1 text-[10px] text-sky-300" title={`Padrão da categoria: ${brl(it.catValue)}`} data-testid={`folha-basesal-level-${l.user_id}`}><UserCog className="h-3 w-3" /> individual</span>}
                                              {it.salLevel === "base" && <span className="rounded bg-emerald-500/10 px-1 text-[10px] text-emerald-300" title="Salário da categoria definido para esta base" data-testid={`folha-basesal-level-${l.user_id}`}>por base</span>}
                                              {it.salLevel === "global" && <span className="rounded bg-slate-800 px-1 text-[10px] text-slate-400" title="Salário global da categoria" data-testid={`folha-basesal-level-${l.user_id}`}>global</span>}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                              <span className={`font-mono text-sm ${it.override ? "text-sky-200" : "text-slate-200"}`} data-testid={`folha-basesal-value-${l.user_id}`}>{brl(it.value)}</span>
                                              {!closed && <button onClick={(e) => { e.stopPropagation(); setSalEdit({ uid: l.user_id, value: String(it.value ?? "") }); }} title="Editar salário base deste colaborador" className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200" data-testid={`folha-basesal-edit-${l.user_id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                                            </span>
                                          </div>
                                        )}
                                        {it.override && salEdit?.uid !== l.user_id && !closed && (
                                          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                                            <span>Categoria{it.salLevel && baseSalOf(l.user_id, it.id) != null ? " (base)" : ""}: {brl(it.catValue)}</span>
                                            <button disabled={salBusy} onClick={(e) => { e.stopPropagation(); saveSalary(l.user_id, null); }} className="text-sky-400 hover:underline" data-testid={`folha-basesal-reset-${l.user_id}`}>Usar padrão</button>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                                        <span className="text-sm text-slate-300">{it.label}</span>
                                        <span className="font-mono text-sm text-slate-200">{brl(it.value)}</span>
                                      </div>
                                    ))}
                                    {g.salario.length === 0 && <span className="text-xs text-slate-600">Sem itens de salário</span>}
                                  </div>
                                </div>
                                <div>
                                  <div className="mb-1.5 flex items-center justify-between">
                                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Benefícios</span>
                                    <div className="flex items-center gap-2">
                                      {!isMensal && !closed && (
                                        <Button size="sm" variant="outline" className="h-7 border-emerald-700/50 px-2 text-xs text-emerald-300 hover:bg-emerald-500/10" onClick={() => setIndivForm({ user_id: l.user_id, label: "", kind: "benefit", value: "" })} data-testid={`indiv-add-${l.user_id}`}>
                                          <Plus className="h-3.5 w-3.5" /> Benefício individual
                                        </Button>
                                      )}
                                      <span className="font-mono text-sm font-semibold text-emerald-300" data-testid={`folha-ben-${l.user_id}`}>{brl(g.sBen + indivBenOf(l.user_id).reduce((a, b) => a + (Number(b.value) || 0), 0))}</span>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                    {g.beneficios.map((it) => (
                                      <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                                        <span className="text-sm text-slate-300">{it.label}</span>
                                        <span className="font-mono text-sm text-emerald-300">{brl(it.value)}</span>
                                      </div>
                                    ))}
                                    {indivBenOf(l.user_id).map((b) => (
                                      <div key={b.id} className="flex items-center justify-between rounded-lg border border-emerald-900/40 bg-slate-900 px-3 py-2" data-testid={`indiv-ben-${b.id}`}>
                                        <span className="truncate text-sm text-slate-300">{b.label} <span className="ml-1 rounded bg-emerald-500/15 px-1 text-[10px] text-emerald-300">individual</span></span>
                                        <span className="flex items-center gap-2">
                                          <span className="font-mono text-sm text-emerald-300">{brl(b.value)}</span>
                                          {!isMensal && !closed && <button onClick={() => delIndiv(b.id)} className="text-slate-500 hover:text-rose-400" data-testid={`indiv-del-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                                        </span>
                                      </div>
                                    ))}
                                    {g.beneficios.length === 0 && indivBenOf(l.user_id).length === 0 && <span className="text-xs text-slate-600">Sem benefícios</span>}
                                  </div>
                                  {indivFormEl(l.user_id)}
                                </div>
                                {(g.descontos.length > 0 || indivDiscOf(l.user_id).length > 0) && (
                                  <div>
                                    <div className="mb-1.5 flex items-center justify-between">
                                      <span className="text-xs font-bold uppercase tracking-wider text-rose-400">Descontos</span>
                                      <span className="font-mono text-sm font-semibold text-rose-300">- {brl(g.sDesc + indivDiscOf(l.user_id).reduce((a, b) => a + (Number(b.value) || 0), 0))}</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                      {g.descontos.map((it) => (
                                        <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                                          <span className="text-sm text-slate-300">{it.label}</span>
                                          <span className="font-mono text-sm text-rose-300">- {brl(it.value)}</span>
                                        </div>
                                      ))}
                                      {indivDiscOf(l.user_id).map((b) => (
                                        <div key={b.id} className="flex items-center justify-between rounded-lg border border-rose-900/40 bg-slate-900 px-3 py-2" data-testid={`indiv-disc-${b.id}`}>
                                          <span className="truncate text-sm text-slate-300">{b.label} <span className="ml-1 rounded bg-rose-500/15 px-1 text-[10px] text-rose-300">individual</span></span>
                                          <span className="flex items-center gap-2">
                                            <span className="font-mono text-sm text-rose-300">- {brl(b.value)}</span>
                                            {!isMensal && !closed && <button onClick={() => delIndiv(b.id)} className="text-slate-500 hover:text-rose-400" data-testid={`indiv-del-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {g.impostos.length > 0 && (
                                  <div data-testid={`folha-impostos-${l.user_id}`}>
                                    <div className="mb-1.5 flex items-center justify-between">
                                      <span className="text-xs font-bold uppercase tracking-wider text-amber-400">Impostos</span>
                                      <span className="font-mono text-sm font-semibold text-amber-300">- {brl(g.sImp)}</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                      {g.impostos.map((it) => (
                                        <div key={it.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                                          <span className="text-sm text-slate-300">{it.label}</span>
                                          <span className="font-mono text-sm text-amber-300">- {brl(it.value)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {(faltasByUser[l.user_id] || []).length > 0 && (
                                  <div data-testid={`folha-faltas-${l.user_id}`}>
                                    <div className="mb-1.5 flex items-center justify-between">
                                      <span className="text-xs font-bold uppercase tracking-wider text-rose-400">Descontos por falta</span>
                                      <span className="font-mono text-sm font-semibold text-rose-300">- {brl(faltaSum(l.user_id))}</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                      {(faltasByUser[l.user_id] || []).map((f) => (
                                        <div key={f.id} className="flex items-center justify-between rounded-lg border border-rose-900/40 bg-slate-900 px-3 py-2">
                                          <span className="text-sm text-slate-300">
                                            Falta {new Date(f.date).toLocaleDateString("pt-BR")}
                                            <span className="ml-1 text-xs text-slate-500">({f.status === "approved" ? "aprovada" : "reprovada"})</span>
                                          </span>
                                          <span className="font-mono text-sm text-rose-300">- {brl(f.discount_amount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div className="space-y-1 border-t border-slate-800 pt-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-slate-200">Total do colaborador</span>
                                    <span className="font-mono text-base font-bold text-white">{brl(lineTotal)}</span>
                                  </div>
                                  {faltaSum(l.user_id) > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-semibold text-rose-300">Total com descontos de faltas</span>
                                      <span className="font-mono text-base font-bold text-rose-300" data-testid={`folha-net-${l.user_id}`}>{brl(lineTotal - faltaSum(l.user_id))}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ); })()}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h3 className="mt-2 text-sm font-bold uppercase tracking-wider text-slate-300" data-testid="folha-pj-heading">PJ</h3>
          <p className="text-xs text-slate-500">Proventos PJ = entregas do período × valor por entrega (personalizado do colaborador ou da categoria em Configuração de salário → Ciclista PJ / Motoboy PJ).</p>
          <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
            <table className="w-full text-sm" data-testid="folha-pj-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Colaborador</th>
                  <th className="px-4 py-3 font-semibold">Entregas</th>
                  <th className="px-4 py-3 font-semibold">Valor/entrega</th>
                  <th className="px-4 py-3 font-semibold">Proventos</th>
                  <th className="px-4 py-3 font-semibold">Benefícios</th>
                  <th className="px-4 py-3 font-semibold">Descontos</th>
                  <th className="px-4 py-3 font-semibold">Impostos</th>
                  <th className="px-4 py-3 font-semibold">Total líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {pjLines.length === 0 && <tr><td colSpan={8}><Empty label="Nenhum colaborador PJ" /></td></tr>}
                {pjLines.map((l) => {
                  const del = deliveriesByUser[l.user_id] || 0;
                  const rate = pjRateOf(l.user_id);
                  const prov = del * rate;
                  const g = pjBreakdown(l.user_id);
                  const ben = g.sBen, dsc = g.sDesc, imp = g.sImp;
                  const net = prov + ben - dsc - imp;
                  const open = expanded === l.user_id;
                  const fs = faltaSum(l.user_id);
                  return (
                    <Fragment key={l.user_id}>
                    <tr className="cursor-pointer hover:bg-slate-800/40" onClick={() => setExpanded(open ? null : l.user_id)} data-testid={`folha-pj-row-${l.user_id}`}>
                      <td className="px-4 py-3 font-medium text-slate-100" data-testid={`folha-pj-name-${l.user_id}`}>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setDetailId(l.user_id); }} data-testid={`folha-pj-name-btn-${l.user_id}`}
                          className="text-left underline-offset-4 transition-colors hover:text-orange-300 hover:underline">{l.name}</button>
                      </td>
                      <td className="px-4 py-3 font-mono text-orange-300" data-testid={`folha-pj-del-${l.user_id}`}>{num(del)}</td>
                      <td className="px-4 py-3 font-mono text-slate-300">{brl(rate)}{pjOverride[l.user_id] != null && <span className="ml-1 rounded bg-sky-500/15 px-1 text-[10px] text-sky-300">pers.</span>}</td>
                      <td className="px-4 py-3 font-mono text-emerald-300" data-testid={`folha-pj-prov-${l.user_id}`}>{brl(prov)}<div className="text-[10px] text-slate-500">{num(del)} × {brl(rate)}</div></td>
                      <td className="px-4 py-3 font-mono text-emerald-300" data-testid={`folha-pj-ben-${l.user_id}`}>{brl(ben)}</td>
                      <td className="px-4 py-3 font-mono text-rose-300" data-testid={`folha-pj-desc-${l.user_id}`}>{dsc ? `- ${brl(dsc)}` : brl(0)}</td>
                      <td className="px-4 py-3 font-mono text-amber-300" data-testid={`folha-pj-imp-${l.user_id}`}>{imp ? `- ${brl(imp)}` : brl(0)}</td>
                      <td className="px-4 py-3 font-mono font-semibold text-white" data-testid={`folha-pj-net-${l.user_id}`}>{brl(net)}</td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-950/50">
                        <td colSpan={8} className="px-4 py-3">
                          <div className="space-y-3" data-testid={`folha-pj-detail-${l.user_id}`}>
                            <div>
                              <div className="mb-1.5 flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Proventos PJ</span>
                                <span className="font-mono text-sm font-semibold text-slate-200" data-testid={`folha-pj-detail-prov-${l.user_id}`}>{brl(prov)}</span>
                              </div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                                  <span className="text-sm text-slate-300">{num(del)} entregas × {brl(rate)}/entrega{pjOverride[l.user_id] != null && <span className="ml-1 rounded bg-sky-500/15 px-1 text-[10px] text-sky-300">pers.</span>}</span>
                                  <span className="font-mono text-sm text-slate-200">{brl(prov)}</span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="mb-1.5 flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Benefícios</span>
                                <div className="flex items-center gap-2">
                                  {!isMensal && !closed && (
                                    <Button size="sm" variant="outline" className="h-7 border-emerald-700/50 px-2 text-xs text-emerald-300 hover:bg-emerald-500/10" onClick={() => setIndivForm({ user_id: l.user_id, label: "", kind: "benefit", value: "" })} data-testid={`indiv-add-${l.user_id}`}>
                                      <Plus className="h-3.5 w-3.5" /> Benefício individual
                                    </Button>
                                  )}
                                  <span className="font-mono text-sm font-semibold text-emerald-300" data-testid={`folha-ben-${l.user_id}`}>{brl(ben)}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {g.ben.map((it) => itemCard(it, "text-emerald-300", false, false))}
                                {g.iB.map((b) => itemCard(b, "text-emerald-300", false, true))}
                                {g.ben.length === 0 && g.iB.length === 0 && <span className="text-xs text-slate-600">Sem benefícios</span>}
                              </div>
                              {indivFormEl(l.user_id)}
                            </div>
                            {(g.desc.length > 0 || g.iD.length > 0) && (
                              <div>
                                <div className="mb-1.5 flex items-center justify-between">
                                  <span className="text-xs font-bold uppercase tracking-wider text-rose-400">Descontos</span>
                                  <span className="font-mono text-sm font-semibold text-rose-300">- {brl(dsc)}</span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                  {g.desc.map((it) => itemCard(it, "text-rose-300", true, false))}
                                  {g.iD.map((b) => itemCard(b, "text-rose-300", true, true))}
                                </div>
                              </div>
                            )}
                            {g.imp.length > 0 && (
                              <div data-testid={`folha-impostos-${l.user_id}`}>
                                <div className="mb-1.5 flex items-center justify-between">
                                  <span className="text-xs font-bold uppercase tracking-wider text-amber-400">Impostos</span>
                                  <span className="font-mono text-sm font-semibold text-amber-300">- {brl(imp)}</span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                  {g.imp.map((it) => itemCard(it, "text-amber-300", true, false))}
                                </div>
                              </div>
                            )}
                            {(faltasByUser[l.user_id] || []).length > 0 && (
                              <div data-testid={`folha-faltas-${l.user_id}`}>
                                <div className="mb-1.5 flex items-center justify-between">
                                  <span className="text-xs font-bold uppercase tracking-wider text-rose-400">Descontos por falta</span>
                                  <span className="font-mono text-sm font-semibold text-rose-300">- {brl(fs)}</span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                  {(faltasByUser[l.user_id] || []).map((f) => itemCard({ id: f.id, label: `Falta ${new Date(f.date).toLocaleDateString("pt-BR")}`, value: f.discount_amount }, "text-rose-300", true, false))}
                                </div>
                              </div>
                            )}
                            <div className="space-y-1 border-t border-slate-800 pt-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-slate-200">Total do colaborador</span>
                                <span className="font-mono text-base font-bold text-white" data-testid={`folha-pj-detail-total-${l.user_id}`}>{brl(net)}</span>
                              </div>
                              {fs > 0 && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-rose-300">Total com descontos de faltas</span>
                                  <span className="font-mono text-base font-bold text-rose-300" data-testid={`folha-net-${l.user_id}`}>{brl(net - fs)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function Folha() {
  const cfg = useFetch("/admin/payroll/config");
  const configs = cfg.data?.configs || {};
  const sets = useFetch("/admin/payroll-settings");
  const sections = sets.data?.sections || {};
  const { selectedBaseId, bases } = useBase();
  const comps = useBaseComponents();
  const sal = useBaseSalaries();
  const baseName = (bases || []).find((b) => b.id === selectedBaseId)?.name || "esta base";
  const onBaseSaved = () => Promise.all([comps.refetch(), sal.refetch()]);
  const baseProps = (k) => ({ baseId: selectedBaseId || null, baseName, baseCfg: selectedBaseId ? comps.data?.[selectedBaseId]?.[k] || null : null, baseSalary: selectedBaseId ? sal.data?.[selectedBaseId]?.[k] ?? null : null, onBaseSaved });
  const [tab, setTabState] = useState(() => sessionStorage.getItem("folha-tab") || "periodo");
  const setTab = (v) => { sessionStorage.setItem("folha-tab", v); setTabState(v); };

  return (
    <div data-testid="folha">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900">
          <TabsTrigger value="periodo" data-testid="folha-tab-periodo"><Wallet className="mr-1.5 h-4 w-4" /> Folha do período</TabsTrigger>
          <TabsTrigger value="config" data-testid="folha-tab-config"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Configuração de salário</TabsTrigger>
        </TabsList>
        <TabsContent value="periodo" className="mt-4"><PeriodView /></TabsContent>
        <TabsContent value="config" className="mt-4">
          {(cfg.loading || sets.loading || comps.isLoading) ? <Loading /> : cfg.error ? <ErrorState error={cfg.error} onRetry={cfg.refetch} /> : sets.error ? <ErrorState error={sets.error} onRetry={sets.refetch} /> : (
            <div className="space-y-5">
              <p className="text-sm text-slate-400">Defina salário base e componentes (vale alimentação, deslocamento, combustível, bônus e descontos) por tipo de colaborador. Componentes automáticos (meal/commute/fuel) usam as regras de Benefícios. A origem <span className="font-semibold text-slate-200">Bônus (auto)</span> calcula o valor automaticamente pelas entregas da quinzena, atribuindo o nível (Bronze→Mestre) conforme os mínimos em Benefícios e o valor da categoria de mesmo nome em “Categorias de desempenho”.</p>
              {ETYPES.map((et) => (
                et.section
                  ? <ConfigEditor key={`${et.key}-${selectedBaseId || "all"}`} {...baseProps(et.key)} etype={et.key} label={et.label} section={et.section} rate={sections[et.section]?.valor_por_entrega} initial={sections[et.section]?.items || []} onSaved={sets.refetch} />
                  : <ConfigEditor key={`${et.key}-${selectedBaseId || "all"}`} {...baseProps(et.key)} etype={et.key} label={et.label} initial={(configs[et.key]?.items) || []} onSaved={cfg.refetch} />
              ))}
              <TiersEditor />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
